/**
 * F-43 — System Health admin page.
 *
 * Three cards backed by GET /api/admin/health/full:
 *   - Probes: db / redis / gcs / llm with green/red dot per probe
 *   - Metrics: queue depth, dead-letters, llm cost, active users, docs pending
 *   - Incidents: last 50 error events with truncated traceback
 */

import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '../lib/api'

interface ProbeResult {
  name: string
  ok: boolean
  latency_ms: number
  last_error: string | null
  note: string | null
}

interface HealthMetrics {
  queue_depth: number
  dead_letter_count: number
  llm_cost_today_usd: number
  llm_cost_30d_usd: number
  active_users_24h: number
  documents_pending: number
  notes: Record<string, string>
}

interface IncidentEntry {
  id: number
  ts: string
  severity: string
  route: string | null
  message: string
  traceback: string | null
}

interface HealthFullResponse {
  generated_at: string
  probes: ProbeResult[]
  metrics: HealthMetrics
  incidents: IncidentEntry[]
}

const cardStyle = {
  borderRadius: 'var(--radius-card)',
  boxShadow: 'var(--shadow-card)',
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      className="inline-block w-3 h-3 rounded-full"
      style={{
        background: ok ? 'var(--brand-verified)' : 'var(--brand-gap)',
        boxShadow: ok ? '0 0 0 2px rgba(15,117,99,.3)' : '0 0 0 2px rgba(179,64,30,.25)',
      }}
      aria-label={ok ? 'ok' : 'down'}
    />
  )
}

function ProbeCard({ probes }: { probes: ProbeResult[] }) {
  return (
    <div className="bg-white p-6" style={cardStyle}>
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Probes</h2>
      <div className="space-y-3">
        {probes.map((p) => (
          <div
            key={p.name}
            className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0"
          >
            <div className="pt-1.5">
              <StatusDot ok={p.ok} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="font-medium text-slate-900 uppercase tracking-wide text-sm">
                  {p.name}
                </div>
                <div className="text-xs text-slate-500">{p.latency_ms.toFixed(1)} ms</div>
              </div>
              {p.note && <div className="text-xs text-slate-500 mt-0.5">{p.note}</div>}
              {p.last_error && (
                <div className="text-xs text-red-600 mt-0.5 font-mono break-all">
                  {p.last_error}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function MetricsCard({ metrics }: { metrics: HealthMetrics }) {
  const items: Array<[string, string | number, string | undefined]> = [
    ['Queue depth', metrics.queue_depth, undefined],
    ['Dead-letters', metrics.dead_letter_count, undefined],
    ['LLM cost (today)', `$${metrics.llm_cost_today_usd.toFixed(2)}`, metrics.notes.llm_cost],
    ['LLM cost (30d)', `$${metrics.llm_cost_30d_usd.toFixed(2)}`, metrics.notes.llm_cost],
    ['Active users (24h)', metrics.active_users_24h, undefined],
    ['Documents pending', metrics.documents_pending, undefined],
  ]
  return (
    <div className="bg-white p-6" style={cardStyle}>
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Metrics</h2>
      <div className="grid grid-cols-2 gap-4">
        {items.map(([label, value, note]) => (
          <div key={label}>
            <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
            {note && <div className="text-xs text-slate-400 mt-1">({note})</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

function IncidentsCard({ incidents }: { incidents: IncidentEntry[] }) {
  return (
    <div className="bg-white p-6" style={cardStyle}>
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent incidents</h2>
      {incidents.length === 0 ? (
        <p className="text-sm text-slate-500">No incidents recorded.</p>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {incidents.map((inc) => (
            <div
              key={inc.id}
              className="text-xs border-l-4 pl-3 py-2"
              style={{
                borderColor:
                  inc.severity === 'error'
                    ? 'var(--brand-gap)'
                    : inc.severity === 'warning'
                      ? 'var(--brand-expired)'
                      : 'var(--brand-muted)',
              }}
            >
              <div className="flex items-center justify-between text-slate-600">
                <span className="font-mono">{new Date(inc.ts).toLocaleString()}</span>
                <span className="uppercase tracking-wide font-semibold">{inc.severity}</span>
              </div>
              {inc.route && <div className="font-mono text-slate-500 mt-0.5">{inc.route}</div>}
              <div className="text-slate-900 mt-1">{inc.message}</div>
              {inc.traceback && (
                <pre className="mt-1 text-[10px] bg-slate-50 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all text-slate-600">
                  {inc.traceback.slice(0, 800)}
                  {inc.traceback.length > 800 ? '...' : ''}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function SystemHealthPage() {
  const [data, setData] = useState<HealthFullResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiFetch<HealthFullResponse>('/api/admin/health/full')
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading && !data) {
    return (
      <div className="bg-white p-6" style={cardStyle}>
        <p className="text-sm text-slate-500">Loading system health…</p>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="bg-white p-6" style={cardStyle}>
        <h2 className="text-lg font-semibold text-slate-900">System Health</h2>
        <p className="text-sm text-red-600 mt-2">{error}</p>
        <button onClick={load} className="mt-3 px-3 py-1.5 text-sm bg-slate-900 text-white rounded">
          Retry
        </button>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">System Health</h1>
          <p className="text-sm text-slate-500">
            Updated {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <ProbeCard probes={data.probes} />
      <MetricsCard metrics={data.metrics} />
      <IncidentsCard incidents={data.incidents} />
    </div>
  )
}
